import React, { useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Dropdown, Typography } from "@mimber/ui-web"
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from "@mimber/ui-web"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import { DeleteCommunicationTemplateModal } from "@/components/modals"
import { Resource, Action } from "@mimber/schema"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems/usePermissionedMenuItems.types"
import type { CommunicationTemplateActionsDropdownProps } from "./CommunicationTemplateActionsDropdown.types"

const { Text } = Typography

export const CommunicationTemplateActionsDropdown: React.FunctionComponent<
  CommunicationTemplateActionsDropdownProps
> = ({
  template,
  onEdit,
  onActionComplete,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const navigate = useNavigate()
  const openDeleteRef = useRef<(() => void) | null>(null)

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      resource: Resource.COMMUNICATION_TEMPLATES,
      action: Action.UPDATE,
      onClick: () => onEdit?.(template),
    },
    {
      key: "duplicate",
      label: "Duplicate",
      icon: <CopyOutlined />,
      resource: Resource.COMMUNICATION_TEMPLATES,
      action: Action.CREATE,
      onClick: () =>
        navigate({
          to: "/communication/templates/new",
          search: { duplicateFrom: template.uuid },
        }),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      resource: Resource.COMMUNICATION_TEMPLATES,
      action: Action.DELETE,
      onClick: () => openDeleteRef.current?.(),
    },
  ]

  const { items } = usePermissionedMenuItems({ items: menuItems })

  if (items.length === 0) {
    return <Text type="secondary">—</Text>
  }

  return (
    <>
      <DeleteCommunicationTemplateModal
        template={template}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          openDeleteRef.current = open
          return <></>
        }}
      />
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button
          type={type}
          icon={iconOnly ? <MoreOutlined /> : undefined}
          size={size}
          data-testid={`template-actions-${template.uuid}`}
        >
          {!iconOnly && "Actions"}
        </Button>
      </Dropdown>
    </>
  )
}
